import React from 'react';

import { Skeleton } from '@mui/material';

import { en } from 'localization';
import {
  PublicContainer,
  PublicFooter,
  PublicMain,
  PublicPageShell,
  PublicSiteHeader,
} from 'shared-components/public-shell';
import { VisuallyHidden } from 'shared-components/state-views';

import {
  HeroInner,
  JobContentColumn,
  JobSummaryCard,
  PublicContentLayout,
  PublicHero,
  SkeletonJobBody,
  SkeletonSummaryList,
  SkeletonSummaryRow,
} from './styled-components';

const copy = en.publicJobPage;

// Varied widths so the loading body reads as prose, not a uniform block.
const BODY_LINE_WIDTHS = [
  '95%',
  '88%',
  '70%',
  '92%',
  '60%',
  '85%',
  '45%',
] as const;

const SKELETON_SUMMARY_ROW_KEYS = [
  'job-detail-summary-loading-row-1',
  'job-detail-summary-loading-row-2',
  'job-detail-summary-loading-row-3',
  'job-detail-summary-loading-row-4',
] as const;

// Used both as the page's isLoading UI and as the route's Suspense fallback,
// so chunk download and data load look like one continuous skeleton. The
// status region announces the load; the decorative bars stay aria-hidden.
export const JobsDetailLoading: React.FC = () => (
  <PublicPageShell>
    <PublicSiteHeader openRoles="show" />

    <PublicMain>
      <div aria-busy="true">
        <VisuallyHidden aria-busy="true" as="output">
          {copy.loading}
        </VisuallyHidden>

        <PublicHero>
          <PublicContainer>
            <HeroInner aria-hidden>
              <Skeleton variant="text" width="8rem" />

              <Skeleton variant="text" width="12rem" />

              <Skeleton
                height="3.25rem"
                variant="text"
                width="min(22ch, 90%)"
              />

              <Skeleton variant="text" width="80%" />

              <Skeleton variant="text" width="55%" />

              <Skeleton height="3rem" variant="rounded" width="13rem" />
            </HeroInner>
          </PublicContainer>
        </PublicHero>

        <PublicContainer>
          <PublicContentLayout>
            <JobContentColumn>
              <SkeletonJobBody aria-hidden>
                {BODY_LINE_WIDTHS.map(width => (
                  <Skeleton key={width} variant="text" width={width} />
                ))}
              </SkeletonJobBody>
            </JobContentColumn>

            <JobSummaryCard aria-hidden>
              <SkeletonSummaryList>
                {SKELETON_SUMMARY_ROW_KEYS.map(key => (
                  <SkeletonSummaryRow key={key}>
                    <Skeleton variant="text" width="35%" />

                    <Skeleton variant="text" width="40%" />
                  </SkeletonSummaryRow>
                ))}
              </SkeletonSummaryList>

              <Skeleton height="3rem" variant="rounded" width="100%" />
            </JobSummaryCard>
          </PublicContentLayout>
        </PublicContainer>
      </div>
    </PublicMain>

    <PublicFooter />
  </PublicPageShell>
);

export default JobsDetailLoading;
